import type { ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';

/**
 * Semantic tones, not colours. Call sites say what a thing *is*
 * (`tone="won"`), never how it should look, so restyling the whole app's
 * notion of "at risk" is a one-line change in theme.css.
 */
export type Tone = 'won' | 'lost' | 'risk' | 'stalled' | 'open' | 'neutral' | 'accent';

const TONE: Record<Tone, { text: string; bg: string; dot: string }> = {
  won: { text: 'text-won', bg: 'bg-won-subtle', dot: 'bg-won' },
  lost: { text: 'text-lost', bg: 'bg-lost-subtle', dot: 'bg-lost' },
  risk: { text: 'text-risk', bg: 'bg-risk-subtle', dot: 'bg-risk' },
  stalled: { text: 'text-stalled', bg: 'bg-stalled-subtle', dot: 'bg-stalled' },
  open: { text: 'text-open', bg: 'bg-open-subtle', dot: 'bg-open' },
  neutral: { text: 'text-neutral', bg: 'bg-neutral-subtle', dot: 'bg-neutral' },
  accent: { text: 'text-accent-text', bg: 'bg-accent-subtle', dot: 'bg-accent-text' },
};

export interface BadgeProps {
  tone?: Tone;
  /** A filled dot ahead of the label, for status in a dense row. */
  dot?: boolean;
  children: ReactNode;
}

export function Badge({ tone = 'neutral', dot = false, children }: BadgeProps) {
  const style = TONE[tone];
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-2xs font-medium whitespace-nowrap',
        style.text,
        style.bg,
      )}
    >
      {dot && <span className={cx('size-1.5 shrink-0 rounded-full', style.dot)} />}
      {children}
    </span>
  );
}

/**
 * Bare dot for the tightest contexts, like a pipeline card corner where a
 * full badge would be noise.
 */
export function StatusDot({ tone = 'neutral', title }: { tone?: Tone; title?: string }) {
  return (
    <span
      className={cx('inline-block size-1.5 shrink-0 rounded-full', TONE[tone].dot)}
      title={title}
      role={title ? 'img' : undefined}
      aria-label={title}
    />
  );
}
